import { Suspense, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stepper, StepperIndicator, StepperItem, StepperSeparator, StepperTrigger } from '@/components/ui/stepper'
import { steps, useStepValidationStrategy, type TSteps } from '@/features/proposta/hooks'
import { useCorretagens } from '@/features/proposta/hooks/use-corretagens'
import type { TPropostaLocationState } from '@/features/proposta/interfaces/proposta.type'
import { propostaSchema, type TPropostaSchemaInput, type TPropostaSchemaOutput } from '@/features/proposta/schemas'
import { useSuspenseSimulacoesPorIds } from '@/features/simulador/brasil/hooks/use-suspense-simulacoes-por-ids'
import { useTypedLocation } from '@/hooks/use-typed-location'
import { PageLayout } from '@/layouts/page'
import { EtapaComprador } from '@/pages/proposta/etapa-comprador'
import { EtapaCompradorSkeleton } from '@/pages/proposta/etapa-comprador/etapa-comprador.skeleton'
import { DetalhesDaPropostaStep } from '@/pages/proposta/etapa-detalhes-da-proposta'
import { InformacoesIniciaisStep } from '@/pages/proposta/etapa-informacoes-iniciais'
import { ProposalFormSkeleton } from '@/pages/proposta/proposta-skeleton'
import ResumoProposta from '@/pages/proposta/resumo-proposta'
import { opcaoVagaEnum } from '@/types/proposta'

type TStepsProposta = Record<
  (typeof steps)[number],
  {
    formKey: keyof TPropostaSchemaInput | 'resumo'
    Step: () => React.ReactNode
  }
>

const stepsProposta: TStepsProposta = {
  InformacoesIniciaisStep: {
    formKey: 'informacoesIniciais',
    Step: InformacoesIniciaisStep,
  },
  CadastroCompradoresStep: {
    formKey: 'cadastroCompradores',
    Step: () => (
      <Suspense fallback={<EtapaCompradorSkeleton />}>
        <EtapaComprador />
      </Suspense>
    ),
  },
  DetalhesDaPropostaStep: {
    formKey: 'detalhesDaProposta',
    Step: DetalhesDaPropostaStep,
  },
  ResumoStep: {
    formKey: 'resumo',
    Step: ResumoProposta,
  },
}

export const PropostaSuspense = () => {
  const [isLoading] = useState(false)
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const simulacoesIds = state?.simulacoesIds
  const { data: simulacoes } = useSuspenseSimulacoesPorIds(simulacoesIds)

  const simulacaoLote = simulacoes.find((simulacao) => simulacao.produto.tipoDoProduto === 'Lote')
  const simulacaoUnidade = simulacoes.find((simulacao) => simulacao.produto.tipoDoProduto === 'Unidade')
  const simulacoesAcessorios = simulacoes.filter((simulacao) => simulacao.produto.tipoDoProduto === 'Acessorio')
  useCorretagens(simulacoesIds)

  const opcaoVaga =
    simulacoesAcessorios.length && !simulacaoUnidade?.produto.acessorioObrigatorio
      ? opcaoVagaEnum.Customizada
      : opcaoVagaEnum.Pendente

  const form = useForm<TPropostaSchemaInput, unknown, TPropostaSchemaOutput>({
    defaultValues: {
      step: 0,
      informacoesIniciais: {
        idExternoBloco: simulacoes[0]!.bloco.idExterno,
        clienteAtendimento: {
          id: simulacoes[0]!.cliente.id,
          cpf: simulacoes[0]!.cliente.cpf,
        },
        vendedorAlocadoCliente: simulacoes.find((s) => s.cliente.vendedorAlocadoId)?.cliente.vendedorAlocadoId ?? '',
        simulacaoLote: simulacaoLote,
        simulacaoUnidade: simulacaoUnidade
          ? {
              ...simulacaoUnidade,
              produto: {
                ...simulacaoUnidade.produto,
                plantaDescricao: null,
              },
            }
          : undefined,
        simulacoesAcessorios: simulacoesAcessorios.length
          ? simulacoesAcessorios.map((simulacaoAcessorio) => ({
              ...simulacaoAcessorio,
              produto: {
                ...simulacaoAcessorio.produto,
                tipoDoProduto: simulacaoAcessorio.produto.tipoDoProduto as 'Unidade' | 'Acessorio',
                plantaDescricao: null,
              },
            }))
          : [],
        opcaoVaga,
        vendedor: {
          id: '',
          possuiDadosDeComissao: false,
        },
        planta: {
          descricao: simulacaoUnidade?.produto.plantaDescricao ?? '',
          id: simulacaoUnidade?.produto.plantaId ?? '',
        },
        vagaIds: [],
      },
      cadastroCompradores: {
        clientes: [],
        clientesJuridicos: [],
        compradores: [],
        procuradores: [],
      },
      detalhesDaProposta: {
        observacoes: '',
        termosDaProposta: '',
        imobiliariasCorretores: { incluirParticipacoes: '' },
        assinaturaContrato: '',
        participacaoCompradores: {
          compradores: [],
          titular: {
            id: '',
            percentualParticipacao: 0,
          },
        },
      },
    },
    mode: 'onChange',
    resolver: zodResolver(propostaSchema),
  })

  const currentStep = form.watch('step') ?? 0
  const CurrentStep = stepsProposta[steps[currentStep] as TSteps]

  const { validateStep } = useStepValidationStrategy(form.trigger)

  const validarStepsIntermediarios = async (fromIndex: number, toIndex: number): Promise<boolean> => {
    for (let i = fromIndex; i < toIndex; i++) {
      const stepKey = steps[i]
      if (!stepKey) return false

      const isValid = await validateStep(stepKey)
      if (!isValid) return false
    }
    return true
  }

  const handleStepChangeOnIconClick = async (indiceDestino?: number) => {
    if (indiceDestino === undefined || indiceDestino === currentStep) return
    const voltarEtapa = indiceDestino < currentStep
    if (voltarEtapa) {
      form.setValue('step', indiceDestino)
      return
    }
    const podeAvancar = await validarStepsIntermediarios(currentStep, indiceDestino)
    if (podeAvancar) {
      form.setValue('step', indiceDestino)
    }
  }
  return (
    <FormProvider {...form}>
      <p className="block pb-4 text-center md:hidden">{t('informacoes_iniciais.titulo_mobile')}</p>
      <div className="bg-foreground mx-auto mb-4 max-w-6xl px-6 py-4">
        <Stepper value={currentStep} onValueChange={(v) => form.setValue('step', v)}>
          {steps.map((step, index) => (
            <StepperItem key={step} step={index} className="not-last:flex-1" loading={isLoading}>
              <StepperTrigger
                onIconClick={() => {
                  handleStepChangeOnIconClick(index)
                }}
              >
                <StepperIndicator iconName="pencil" />
              </StepperTrigger>
              {index < steps.length && <StepperSeparator />}
            </StepperItem>
          ))}
        </Stepper>
      </div>
      <CurrentStep.Step />
    </FormProvider>
  )
}

export const PropostaPage = () => {
  return (
    <PageLayout className="mt-6 px-0 pb-0">
      <Suspense fallback={<ProposalFormSkeleton />}>
        <PropostaSuspense />
      </Suspense>
    </PageLayout>
  )
}
